/* eslint-disable max-len */
/* src/components/Contact/Contact.jsx */
import { useState } from 'react';
import { motion } from 'framer-motion';
import {
  FaMapMarkerAlt, FaPhoneAlt, FaEnvelope, FaChevronRight
} from 'react-icons/fa';
import Swal from 'sweetalert2';

// Définition des couleurs modernes
const colors = {
  primary: '#3a86ff',
  secondary: '#ff006e',
  accent: '#8338ec',
  success: '#06d6a0',
  dark: '#1e2a3a',
  darkBlue: '#0f172a',
  light: '#fcfcfc',
  glass: 'rgba(255, 255, 255, 0.1)',
  glassDark: 'rgba(14, 25, 45, 0.85)',
};

const fontFamily = '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif';

const sectionStyle = {
  padding: '120px 40px',
  background: 'linear-gradient(135deg, #f8f9fa 0%, #f1f5f9 100%)',
  position: 'relative',
  overflow: 'hidden',
  fontFamily,
};

const containerStyle = {
  maxWidth: '1200px',
  margin: '0 auto',
  position: 'relative',
  zIndex: 5,
};

const headingStyle = {
  fontSize: 'clamp(2.2rem, 5vw, 3rem)',
  fontWeight: '800',
  textAlign: 'center',
  color: colors.dark,
  marginBottom: '15px',
  position: 'relative',
};

const subtitleStyle = {
  fontSize: 'clamp(1rem, 1.5vw, 1.2rem)',
  color: '#64748b',
  textAlign: 'center',
  maxWidth: '700px',
  margin: '0 auto 60px',
  lineHeight: '1.6',
};

const flexContainerStyle = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: '40px',
  justifyContent: 'center',
};

const formContainerStyle = {
  flex: '1 1 550px',
  background: '#ffffff',
  padding: '50px',
  borderRadius: '24px',
  boxShadow: '0 30px 60px rgba(0, 0, 0, 0.1)',
  border: '1px solid rgba(0, 0, 0, 0.05)',
  position: 'relative',
  overflow: 'hidden',
};

const infoContainerStyle = {
  flex: '1 1 350px',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  gap: '30px',
  maxWidth: '450px',
};

const inputStyle = {
  width: '100%',
  padding: '18px 20px',
  margin: '8px 0 20px',
  border: 'none',
  background: '#f1f5f9',
  borderRadius: '12px',
  fontSize: '1rem',
  fontFamily,
  transition: 'all 0.3s ease',
  boxShadow: 'inset 0 2px 4px rgba(0, 0, 0, 0.03)',
};

const focusedInputStyle = {
  ...inputStyle,
  borderLeft: `4px solid ${colors.primary}`,
  boxShadow: '0 0 0 2px rgba(58, 134, 255, 0.1)'
};

const buttonStyle = {
  background: `linear-gradient(135deg, ${colors.primary}, ${colors.accent})`,
  color: colors.light,
  fontWeight: '600',
  padding: '18px 32px',
  fontSize: '1rem',
  border: 'none',
  borderRadius: '12px',
  cursor: 'pointer',
  boxShadow: '0 10px 25px rgba(58, 134, 255, 0.3)',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '10px',
  width: '100%',
  position: 'relative',
  overflow: 'hidden',
  transition: 'all 0.3s ease',
};

const labelStyle = {
  display: 'block',
  marginBottom: '8px',
  fontWeight: '600',
  fontSize: '0.95rem',
  color: colors.dark,
};

const contactCardStyle = {
  background: colors.darkBlue,
  color: colors.light,
  padding: '30px',
  borderRadius: '20px',
  boxShadow: '0 15px 30px rgba(0, 0, 0, 0.15)',
  display: 'flex',
  flexDirection: 'column',
  gap: '25px',
  position: 'relative',
  overflow: 'hidden',
};

const contactHeadingStyle = {
  fontSize: '1.5rem',
  fontWeight: '700',
  marginBottom: '15px',
  color: colors.light,
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
};

const contactItemStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '15px',
  marginBottom: '15px',
  position: 'relative',
  zIndex: 2,
};

const contactIconStyle = {
  width: '50px',
  height: '50px',
  borderRadius: '12px',
  background: 'rgba(58, 134, 255, 0.15)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: colors.primary,
  fontSize: '20px',
};

const circleShape = {
  position: 'absolute',
  borderRadius: '50%',
  background: 'rgba(58, 134, 255, 0.15)',
  filter: 'blur(40px)',
  zIndex: 0,
  opacity: 0.5,
};

function Contact() {
  const [formState, setFormState] = useState({
    name: '',
    email: '',
    message: '',
  });
  const [focusedField, setFocusedField] = useState(null);

  const handleChange = (e) => {
    setFormState({ ...formState, [e.target.name]: e.target.value });
  };

  const handleFocus = (field) => {
    setFocusedField(field);
  };

  const handleBlur = () => {
    setFocusedField(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch('https://drivesoft-e728p.ondigitalocean.app/contact/send', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formState),
      });

      if (response.ok) {
        Swal.fire({
          icon: 'success',
          title: 'Message envoyé',
          text: 'Le message a été envoyé avec succès.',
          confirmButtonColor: colors.primary,
        });
        setFormState({ name: '', email: '', message: '' });
      }
      else {
        Swal.fire({
          icon: 'error',
          title: 'Erreur',
          text: 'Erreur lors de l\'envoi du message.',
          confirmButtonColor: colors.primary,
        });
      }
    }
    catch (error) {
      console.error('Erreur lors de l\'envoi du message:', error);
      Swal.fire({
        icon: 'error',
        title: 'Erreur',
        text: 'Erreur lors de l\'envoi du message.',
        confirmButtonColor: colors.primary,
      });
    }
  };

  return (
    <section style={sectionStyle}>
      {/* Formes d'arrière-plan */}
      <div style={{
        ...circleShape,
        width: '300px',
        height: '300px',
        top: '10%',
        right: '5%',
      }}
      />
      <div style={{
        ...circleShape,
        width: '400px',
        height: '400px',
        bottom: '5%',
        left: '-150px',
        background: 'rgba(255, 0, 110, 0.1)',
      }}
      />

      <div style={containerStyle}>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
        >
          <h2 style={headingStyle}>
            Contactez-nous
            <motion.div
              style={{
                width: '80px',
                height: '4px',
                background: `linear-gradient(90deg, ${colors.primary}, ${colors.accent})`,
                borderRadius: '2px',
                position: 'absolute',
                bottom: '-10px',
                left: '50%',
                transform: 'translateX(-50%)',
              }}
              initial={{ width: 0 }}
              whileInView={{ width: '80px' }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.5 }}
            />
          </h2>
          <p style={subtitleStyle}>
            Vous avez des questions sur Drivesoft ? Notre équipe est prête à vous accompagner pour trouver la solution adaptée à vos besoins.
          </p>
        </motion.div>

        <div style={flexContainerStyle}>
          <motion.div
            style={formContainerStyle}
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            id="form"
          >
            {/* Décoration d'arrière-plan */}
            <div style={{
              position: 'absolute',
              top: 0,
              right: 0,
              width: '150px',
              height: '150px',
              background: 'radial-gradient(circle, rgba(58, 134, 255, 0.1) 0%, rgba(255, 255, 255, 0) 70%)',
              zIndex: 0
            }}
            />
            <div style={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              width: '120px',
              height: '120px',
              background: 'radial-gradient(circle, rgba(255, 0, 110, 0.1) 0%, rgba(255, 255, 255, 0) 70%)',
              zIndex: 0
            }}
            />

            <form onSubmit={handleSubmit} style={{ position: 'relative', zIndex: 2 }}>
              <div>
                <label htmlFor="name" style={labelStyle}>Nom complet</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  placeholder="Votre nom"
                  required
                  value={formState.name}
                  onChange={handleChange}
                  onFocus={() => handleFocus('name')}
                  onBlur={handleBlur}
                  style={focusedField === 'name' ? focusedInputStyle : inputStyle}
                />
              </div>
              <div>
                <label htmlFor="email" style={labelStyle}>Email</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  placeholder="Votre email"
                  required
                  value={formState.email}
                  onChange={handleChange}
                  onFocus={() => handleFocus('email')}
                  onBlur={handleBlur}
                  style={focusedField === 'email' ? focusedInputStyle : inputStyle}
                />
              </div>
              <div>
                <label htmlFor="message" style={labelStyle}>Message</label>
                <textarea
                  id="message"
                  name="message"
                  placeholder="Décrivez votre projet ou posez-nous vos questions"
                  required
                  value={formState.message}
                  onChange={handleChange}
                  onFocus={() => handleFocus('message')}
                  onBlur={handleBlur}
                  rows="5"
                  style={focusedField === 'message' ? focusedInputStyle : inputStyle}
                />
              </div>
              <motion.button
                type="submit"
                style={buttonStyle}
                whileHover={{
                  scale: 1.02,
                  boxShadow: '0 15px 30px rgba(58, 134, 255, 0.4)'
                }}
                whileTap={{ scale: 0.98 }}
              >
                <span>Envoyer le message</span>
                <FaChevronRight />

                {/* Animated gradient overlay */}
                <motion.div
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0))',
                    zIndex: -1,
                    borderRadius: '12px',
                    opacity: 0
                  }}
                  animate={{
                    x: ['-100%', '100%'],
                    opacity: [0, 0.5, 0]
                  }}
                  transition={{
                    repeat: Infinity,
                    duration: 3,
                    ease: 'linear'
                  }}
                />
              </motion.button>
            </form>
          </motion.div>

          <motion.div
            style={infoContainerStyle}
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.2 }}
          >
            <motion.div
              style={contactCardStyle}
              whileHover={{ y: -5, boxShadow: '0 25px 50px rgba(0, 0, 0, 0.2)' }}
              transition={{ duration: 0.3 }}
            >
              {/* Background glow effect */}
              <div style={{
                position: 'absolute',
                top: '-50px',
                right: '-50px',
                width: '150px',
                height: '150px',
                borderRadius: '50%',
                background: 'rgba(58, 134, 255, 0.2)',
                filter: 'blur(50px)',
                opacity: 0.8,
                zIndex: 0,
              }}
              />
              <div style={{
                position: 'absolute',
                bottom: '-30px',
                left: '-30px',
                width: '100px',
                height: '100px',
                borderRadius: '50%',
                background: 'rgba(255, 0, 110, 0.2)',
                filter: 'blur(40px)',
                opacity: 0.6,
                zIndex: 0,
              }}
              />

              <h3 style={contactHeadingStyle}>
                <span style={{
                  background: `linear-gradient(90deg, ${colors.primary}, ${colors.accent})`,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
                >
                  Informations de contact
                </span>
              </h3>

              <div style={contactItemStyle}>
                <div style={{
                  ...contactIconStyle,
                  background: 'rgba(58, 134, 255, 0.15)',
                  color: colors.primary,
                }}
                >
                  <FaMapMarkerAlt />
                </div>
                <div>
                  <h4 style={{
                    fontWeight: '600', fontSize: '1.1rem', color: '#fff', marginBottom: '5px'
                  }}
                  >
                    Adresse
                  </h4>
                  <p style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.95rem' }}>
                    3 rue Marlène Dietrich, Herblay-sur-Seine
                  </p>
                </div>
              </div>

              <div style={contactItemStyle}>
                <div style={{
                  ...contactIconStyle,
                  background: 'rgba(255, 0, 110, 0.15)',
                  color: colors.secondary,
                }}
                >
                  <FaPhoneAlt />
                </div>
                <div>
                  <h4 style={{
                    fontWeight: '600', fontSize: '1.1rem', color: '#fff', marginBottom: '5px'
                  }}
                  >
                    Téléphone
                  </h4>
                  <p style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.95rem' }}>
                    06 52 80 77 36
                  </p>
                </div>
              </div>

              <div style={contactItemStyle}>
                <div style={{
                  ...contactIconStyle,
                  background: 'rgba(131, 56, 236, 0.15)',
                  color: colors.accent,
                }}
                >
                  <FaEnvelope />
                </div>
                <div>
                  <h4 style={{
                    fontWeight: '600', fontSize: '1.1rem', color: '#fff', marginBottom: '5px'
                  }}
                  >
                    Email
                  </h4>
                  <p style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.95rem' }}>
                    contact@drivesoft.fr
                  </p>
                </div>
              </div>

              <div style={{
                marginTop: '20px',
                padding: '20px',
                background: 'rgba(255, 255, 255, 0.03)',
                backdropFilter: 'blur(10px)',
                borderRadius: '12px',
                border: '1px solid rgba(255, 255, 255, 0.05)',
              }}
              >
                <p style={{ fontSize: '0.95rem', color: 'rgba(255, 255, 255, 0.8)', lineHeight: '1.6' }}>
                  Notre équipe est disponible du lundi au vendredi de 9h à 18h pour répondre à toutes vos questions.
                </p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

export default Contact;
